import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../lib/api';
import { SignIn } from './SignIn';
import '../styles/admin.css';

/**
 * Instructor console — SPEC §11 phase 2: sign in, import a question set, see
 * what is stored. Session creation and the live controls land in phase 3.
 */
export function Admin() {
  const [signedIn, setSignedIn] = useState(api.loadToken() !== null);

  // A token can be present but rejected — expired, or signed with a rotated
  // secret. Confirming with the server avoids showing a console that then fails
  // on its first real request.
  useEffect(() => {
    if (!signedIn) return;
    api.checkSession().catch(() => setSignedIn(false));
  }, [signedIn]);

  if (!signedIn) return <SignIn onSignedIn={() => setSignedIn(true)} />;

  return (
    <div className="admin-shell">
      <header className="admin-bar">
        <span className="admin-bar__mark">Examatron™</span>
        <button
          className="btn btn--quiet"
          style={{ minHeight: 40 }}
          onClick={() => {
            api.clearToken();
            setSignedIn(false);
          }}
        >
          Sign out
        </button>
      </header>

      <main className="admin-main">
        <Sessions />
        <QuestionSets />
      </main>
    </div>
  );
}

/**
 * Start a session and get its join code. Everything past this point happens on
 * the run screen, which is where the room's state lives.
 */
function Sessions() {
  const [sets, setSets] = useState<api.QuestionSetSummary[]>([]);
  const [sessions, setSessions] = useState<api.SessionSummary[]>([]);
  const [qsetId, setQsetId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api
      .listSessions()
      .then((r) => setSessions(r.sessions))
      .catch((err: Error) => setError(err.message));
    api
      .listSets()
      .then((r) => {
        setSets(r.sets);
        setQsetId((prev) => prev || (r.sets[0]?.qsetId ?? ''));
      })
      .catch(() => {});
  }, []);

  useEffect(refresh, [refresh]);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const session = await api.createSession({ qsetId });
      // Straight to the controls — a created session with nobody looking at it
      // is not a useful place to leave someone standing in front of a class.
      window.location.href = `/run/${session.sessionId}`;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start a session.');
      setBusy(false);
    }
  }

  const live = sessions.filter((s) => s.status !== 'COMPLETED');

  return (
    <section className="panel">
      <div>
        <h2>Run a session</h2>
        <p className="panel__hint">
          Creates a join code. Nothing appears on anyone&apos;s screen until you press Next
          Question.
        </p>
      </div>

      {error && <div className="notice notice--bad">{error}</div>}

      {sets.length === 0 ? (
        <p className="panel__hint">Import a question set first.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-3)', alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, minWidth: 240 }}>
            <label htmlFor="qset">Question set</label>
            <select
              id="qset"
              value={qsetId}
              onChange={(e) => setQsetId(e.target.value)}
              style={{
                minHeight: 'var(--tap-min)',
                padding: '0 var(--s-4)',
                color: 'var(--warm)',
                background: 'var(--ink)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--r-md)',
                font: 'inherit',
              }}
            >
              {sets.map((s) => (
                <option key={s.qsetId} value={s.qsetId}>
                  {s.examName} · {s.total} questions
                </option>
              ))}
            </select>
          </div>
          <button className="btn" disabled={busy || !qsetId} onClick={start}>
            {busy ? 'Starting…' : 'Start session'}
          </button>
        </div>
      )}

      {live.length > 0 && (
        <div className="set-table-wrap">
          <table className="set-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Session</th>
                <th>Status</th>
                <th>Started</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {live.map((s) => (
                <tr key={s.sessionId}>
                  <td className="tabular">{s.code}</td>
                  <td style={{ whiteSpace: 'normal' }}>{s.name}</td>
                  <td>{s.status.toLowerCase()}</td>
                  <td className="tabular">{new Date(s.createdAt).toLocaleString()}</td>
                  <td>
                    <a href={`/run/${s.sessionId}`} style={{ color: 'var(--accent)' }}>
                      Open
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function QuestionSets() {
  const [sets, setSets] = useState<api.QuestionSetSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api
      .listSets()
      .then((r) => setSets(r.sets))
      .catch((err: Error) => setError(err.message));
  }, []);

  useEffect(refresh, [refresh]);

  return (
    <>
      <Import onImported={refresh} />

      <section className="panel">
        <div>
          <h2>Question sets</h2>
          <p className="panel__hint">What is currently stored and available to a session.</p>
        </div>

        {error && <div className="notice notice--bad">{error}</div>}

        {sets === null && !error && <p className="panel__hint">Loading…</p>}

        {sets?.length === 0 && (
          <p className="panel__hint">Nothing imported yet. Drop a question set above.</p>
        )}

        {sets && sets.length > 0 && (
          <div className="set-table-wrap">
            <table className="set-table">
              <thead>
                <tr>
                  <th>Set</th>
                  <th>Exam</th>
                  <th className="tabular">Questions</th>
                  <th>Domains</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {sets.map((s) => (
                  <tr key={s.qsetId}>
                    <td>{s.qsetId}</td>
                    <td style={{ whiteSpace: 'normal' }}>{s.examName}</td>
                    <td className="tabular">{s.total}</td>
                    <td>
                      <span className="domain-chips">
                        {s.domains.map((d) => (
                          <span className="chip tabular" key={d.number} title={d.name}>
                            D{String(d.number).padStart(2, '0')} · {d.count}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="tabular">{new Date(s.updatedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/** Slug from a filename: `e01-dop-c02.json` -> `e01-dop-c02`. */
const idFromFilename = (name: string) =>
  name
    .replace(/\.json$/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

function Import({ onImported }: { onImported: () => void }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<api.ImportResult | null>(null);
  const [failure, setFailure] = useState<api.ApiFailure | Error | null>(null);

  async function take(file: File) {
    setBusy(true);
    setResult(null);
    setFailure(null);
    try {
      const parsed = JSON.parse(await file.text()) as unknown;
      setResult(await api.importSet(idFromFilename(file.name), parsed));
      onImported();
    } catch (err) {
      setFailure(err instanceof Error ? err : new Error('Import failed.'));
    } finally {
      setBusy(false);
    }
  }

  const issues = failure instanceof api.ApiFailure ? failure.issues : undefined;

  return (
    <section className="panel">
      <div>
        <h2>Import a question set</h2>
        <p className="panel__hint">
          A JSON file. The set ID comes from the filename, and importing the same ID again
          replaces that set outright — that is how a corrected question gets in.
        </p>
      </div>

      <div
        className={over ? 'dropzone dropzone--over' : 'dropzone'}
        role="button"
        tabIndex={0}
        onClick={() => fileInput.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const file = e.dataTransfer.files[0];
          if (file) void take(file);
        }}
      >
        <span style={{ color: 'var(--warm)', fontWeight: 500 }}>
          {busy ? 'Importing…' : 'Drop a .json file, or click to choose one'}
        </span>
        <span className="panel__hint">
          Every problem is reported at once and nothing is written unless the whole file passes.
        </span>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void take(file);
          e.target.value = ''; // so re-picking the same file fires again
        }}
      />

      {result && (
        <div className="notice notice--good">
          <strong>
            {result.replaced ? 'Replaced' : 'Imported'} {result.qsetId}
          </strong>{' '}
          — {result.questions} questions
          {result.questionsRemoved > 0 && `, ${result.questionsRemoved} removed`}.
          <div className="domain-chips" style={{ marginTop: 'var(--s-3)' }}>
            {result.domains.map((d) => (
              <span className="chip tabular" key={d.number}>
                D{String(d.number).padStart(2, '0')} · {d.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {failure && (
        <div className="notice notice--bad">
          <strong>{failure.message}</strong>
          {issues && (
            <ul className="issues">
              {issues.map((i, n) => (
                <li key={n}>
                  {i.id && <span className="tabular">{i.id} — </span>}
                  {i.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
